import { useCallback, useEffect, useState } from 'react';
import type { HabitView } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { ChevronLeft, Plus } from '@ui/shared/icons';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { CollapsibleGroup } from '@ui/shared/primitives/CollapsibleGroup';
import { habitCategoryMeta } from '@ui/shared/habits/habitCategory';

/**
 * Habits — desktop tree (mirrors `ObjectivesScreenDesktop`'s established
 * catalog convention: 1600px content width, header with title + count +
 * create-button, `repeat(auto-fit, minmax(300px, 1fr))` card grid for the
 * primary group, `CollapsibleGroup` for the secondary ones). A genuinely
 * separate desktop destination from `HabitListScreen.tsx` (phone), same
 * posture as the Objectives desktop screen relative to its own phone
 * sibling — not a restyle, a second tree over the same DomainApi.
 *
 * Same "management screen, not a dashboard" rule this project already
 * applies to Objectives: no stat-strip, no adherence math computed here.
 * `HabitView` (the only shape `listHabits` returns) carries no progress/
 * adherence field at all — confirmed directly against
 * `src/contracts/domain-api.ts` — so there is nothing for a per-card
 * progress indicator to render; inventing one would mean computing
 * adherence in a component, which this project's domain-api-only rule
 * forbids. A future card-level "this week" indicator is a contract gap for
 * the architect/dev-core-domain, not something to fake here.
 */
export function HabitsScreenDesktop({
  onOpenHabit,
  onCreateHabit,
  onBack,
}: {
  readonly onOpenHabit: (habitId: string) => void;
  readonly onCreateHabit: () => void;
  readonly onBack?: () => void;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();
  const [habits, setHabits] = useState<readonly HabitView[] | null>(null);

  const load = useCallback(async () => {
    const list = await api.listHabits({ includeArchived: true });
    setHabits(list);
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, version]);

  const active = habits?.filter((h) => h.state === 'active') ?? [];
  const paused = habits?.filter((h) => h.state === 'paused') ?? [];
  const archived = habits?.filter((h) => h.state === 'archived') ?? [];

  return (
    <main aria-label="Habits" style={{ padding: 'var(--space-8) var(--layout-page-margin-desktop) var(--space-16)', maxWidth: '1600px', margin: '0 auto' }}>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to Today"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            color: 'var(--text-muted)',
            fontSize: 'var(--type-caption-size)',
            fontWeight: 600,
            minHeight: 'var(--touch-target-min)',
            marginLeft: '-8px',
            marginBottom: 'var(--space-4)',
          }}
        >
          <ChevronLeft size={16} aria-hidden="true" />
          Today
        </button>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 'var(--type-display-size)', fontWeight: 'var(--type-display-weight)', color: 'var(--text-primary)' }}>
            Habits
          </h1>
          {habits !== null && (
            <p style={{ marginTop: 'var(--space-1)', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
              {habits.length === 0 ? 'No habits yet.' : `${habits.length} total.`}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onCreateHabit}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-4)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--hue-primary-vivid)',
            border: '1px solid var(--hue-primary-strong)',
            color: 'var(--text-on-accent)',
            fontWeight: 600,
            fontSize: 'var(--type-body-size)',
          }}
        >
          <Plus size={16} aria-hidden="true" />
          New habit
        </button>
      </div>

      {habits === null ? (
        <div style={{ marginTop: 'var(--space-6)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-5)' }}>
          <SkeletonBlock height={140} />
          <SkeletonBlock height={140} />
          <SkeletonBlock height={140} />
        </div>
      ) : habits.length === 0 ? (
        <p style={{ marginTop: 'var(--space-6)', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          No habits yet. Set up your first one to see it on your daily page.
        </p>
      ) : (
        <>
          {active.length > 0 ? (
            <div style={{ marginTop: 'var(--space-6)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-5)' }}>
              {active.map((habit) => (
                <HabitCard key={habit.habitId} habit={habit} onSelect={() => onOpenHabit(habit.habitId)} />
              ))}
            </div>
          ) : (
            <p style={{ marginTop: 'var(--space-6)', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
              No active habits right now.
            </p>
          )}

          {paused.length > 0 && (
            <CollapsibleGroup label="Paused">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-5)' }}>
                {paused.map((habit) => (
                  <HabitCard key={habit.habitId} habit={habit} onSelect={() => onOpenHabit(habit.habitId)} />
                ))}
              </div>
            </CollapsibleGroup>
          )}

          {archived.length > 0 && (
            <CollapsibleGroup label="Archived">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-5)' }}>
                {archived.map((habit) => (
                  <HabitCard key={habit.habitId} habit={habit} onSelect={() => onOpenHabit(habit.habitId)} />
                ))}
              </div>
            </CollapsibleGroup>
          )}
        </>
      )}
    </main>
  );
}

function HabitCard({
  habit,
  onSelect,
}: {
  readonly habit: HabitView;
  readonly onSelect: () => void;
}) {
  const meta = habitCategoryMeta(habit.category);
  const frequency = describeFrequency(habit);
  const isActive = habit.state === 'active';

  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-xl)',
        background: isActive ? 'var(--surface-raised)' : 'var(--surface-base)',
        border: '1px solid var(--border-subtle)',
        boxShadow: isActive ? 'var(--shadow-1)' : 'none',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '44px',
          height: '44px',
          borderRadius: 'var(--radius-md)',
          background: isActive
            ? `color-mix(in srgb, var(--accent-${meta.tokenSuffix}-vivid) 16%, var(--surface-raised))`
            : 'var(--surface-hover)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
        }}
      >
        <meta.Icon size={20} color={isActive ? `var(--accent-${meta.tokenSuffix})` : 'var(--text-muted)'} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 'var(--type-heading-size)', fontWeight: 600, color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
          {habit.name}
        </div>
        <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: 'var(--space-1)' }}>
          {habit.plan.habitKind === 'build' ? 'Build' : 'Avoid'} · {frequency}
        </div>
      </div>
      {!isActive && (
        <span
          style={{
            fontSize: 'var(--type-caption-size)',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            background: 'var(--surface-hover)',
            padding: '2px 10px',
            borderRadius: 'var(--radius-pill)',
            flex: 'none',
          }}
        >
          {habit.state === 'paused' ? 'Paused' : 'Archived'}
        </span>
      )}
    </button>
  );
}

function describeFrequency(habit: HabitView): string {
  if (habit.plan.habitKind === 'build') {
    return habit.plan.schedule.kind === 'daily' ? 'Daily' : `${habit.plan.schedule.targetPerWeek}× a week`;
  }
  const limit = habit.plan.schedule.limitPerWeek;
  return limit === 0 ? 'Zero this week' : `At most ${limit} a week`;
}
